/**
 * PricingPreview — live deterministic pricing preview for a rate card against
 * a sample resource model + region/sector/size keys.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Plus, RefreshCw, TrendingUp, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "../../../lib/api";
import NumInput from "./NumInput";

export default function PricingPreview({ cardId, currency }) {
  const [resourceModel, setResourceModel] = useState({
    "Engagement Lead": 2,
    "Senior Consultant": 4,
    "Business Analyst": 3,
  });
  const [region, setRegion] = useState("uk");
  const [sector, setSector] = useState("financial_services");
  const [size, setSize] = useState("mid_market");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const updateRole = (role, value) => {
    if (value === "" || value === null) {
      const next = { ...resourceModel };
      delete next[role];
      setResourceModel(next);
    } else {
      setResourceModel({ ...resourceModel, [role]: Number(value) });
    }
  };

  const run = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/rate-cards/preview-pricing", {
        rate_card_id: cardId,
        resource_model: resourceModel,
        region,
        sector,
        size,
      });
      setResult(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Pricing preview failed");
    } finally {
      setBusy(false);
    }
  }, [cardId, resourceModel, region, sector, size]);

  useEffect(() => { run(); /* eslint-disable-next-line */ }, [cardId]);

  return (
    <div className="border border-zinc-200 bg-white p-4 space-y-3" data-testid="pricing-preview">
      <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-zinc-700 flex items-center gap-2">
        <TrendingUp size={12}/> Live pricing preview
      </div>
      <div className="grid grid-cols-3 gap-2 text-[12px]">
        <input
          value={region}
          onChange={(e) => setRegion(e.target.value)}
          placeholder="region key (e.g. uk)"
          className="border border-zinc-300 px-2 py-1"
          data-testid="preview-region"
        />
        <input
          value={sector}
          onChange={(e) => setSector(e.target.value)}
          placeholder="sector key"
          className="border border-zinc-300 px-2 py-1"
          data-testid="preview-sector"
        />
        <input
          value={size}
          onChange={(e) => setSize(e.target.value)}
          placeholder="size key"
          className="border border-zinc-300 px-2 py-1"
          data-testid="preview-size"
        />
      </div>
      <div className="text-[12px]">
        <div className="text-zinc-500 mb-1">Sample resource model (FTE-weeks per role)</div>
        {Object.entries(resourceModel).map(([role, fte]) => (
          <div key={role} className="flex items-center gap-2 mb-1">
            <input
              value={role}
              onChange={(e) => {
                const next = { ...resourceModel };
                delete next[role];
                next[e.target.value] = fte;
                setResourceModel(next);
              }}
              className="flex-1 border border-zinc-300 px-2 py-1 text-[12px]"
            />
            <NumInput value={fte} onChange={(v) => updateRole(role, v)} suffix="fte-weeks" min={0} max={50} step={0.5} />
          </div>
        ))}
        <button
          type="button"
          onClick={() => setResourceModel({ ...resourceModel, "New role": 1 })}
          className="text-[11px] text-[#7737F6] hover:text-[#5D28C7] inline-flex items-center gap-1 mt-1"
        >
          <Plus size={11}/> add role
        </button>
      </div>
      <button
        type="button"
        onClick={run}
        disabled={busy}
        className="w-full bg-[#7737F6] hover:bg-[#5D28C7] disabled:opacity-60 text-white text-[12px] font-medium px-3 py-1.5 inline-flex items-center justify-center gap-2"
        data-testid="preview-run"
      >
        {busy ? <RefreshCw size={12} className="animate-spin"/> : <TrendingUp size={12}/>}
        Recompute
      </button>
      {result && (
        <div className="border-t border-zinc-200 pt-3 text-[12.5px]">
          <div className="flex justify-between items-baseline">
            <span className="text-zinc-500">Total</span>
            <span className="text-[20px] font-semibold text-[#7737F6] tabular-nums">{currency} {Number(result.total_gbp).toLocaleString("en-GB")}</span>
          </div>
          <div className="text-[10.5px] text-zinc-500 mt-1">
            Composite multiplier ×{result.modifiers?.composite ?? 1}
            {result.fallback_roles?.length > 0 && (
              <span className="ml-2 text-amber-700 inline-flex items-center gap-1">
                <AlertTriangle size={10}/> {result.fallback_roles.length} unknown role{result.fallback_roles.length === 1 ? "" : "s"}
              </span>
            )}
          </div>
          <table className="w-full text-[11.5px] mt-2">
            <tbody>
              {result.per_role?.map((r, i) => (
                <tr key={i} className="border-b border-zinc-100">
                  <td className="py-1 pr-2">
                    {r.role}
                    {!r.matched && <span className="ml-1 text-[10px] text-amber-700">fallback</span>}
                  </td>
                  <td className="py-1 text-right text-zinc-600 tabular-nums">£{r.day_rate}/d</td>
                  <td className="py-1 text-right text-zinc-600 tabular-nums">{r.billable_days}d</td>
                  <td className="py-1 text-right font-medium tabular-nums">£{Math.round(r.line_cost).toLocaleString("en-GB")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
